import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User } from '../../services/authService';
import { KnowledgeCategoryService, KnowledgeCategory } from '../../services/knowledgeCategoryService';
import PageHeader from '../common/PageHeader';
import CategoryForm from './CategoryForm';
import './KnowledgeCategories.css';

interface KnowledgeCategoriesProps {
  user: User | null;
  onClose: () => void;
  onToggleSidebar: () => void;
}

const KnowledgeCategories: React.FC<KnowledgeCategoriesProps> = ({
  user,
  onClose,
  onToggleSidebar
}) => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState<KnowledgeCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState<KnowledgeCategory | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'my' | 'other' | 'favorites'>('my');

  useEffect(() => {
    loadCategories();
  }, []);

  // Auto-switch to "other" tab if no my categories but other categories exist
  useEffect(() => {
    if (!loading && categories.length > 0 && user) {
      const myCategories = categories.filter(cat => String(cat.user_id) === String(user.id));
      const otherCategories = categories.filter(cat => String(cat.user_id) !== String(user.id));
      if (myCategories.length === 0 && otherCategories.length > 0 && activeTab === 'my') {
        setActiveTab('other');
      }
    }
  }, [categories, loading, user, activeTab]);

  const loadCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await KnowledgeCategoryService.getCategories();
      setCategories(result.categories);
    } catch (err) {
      console.error('Error loading categories:', err);
      setError(err instanceof Error ? err.message : 'Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCategory = () => {
    setEditingCategory(null);
    setShowCategoryForm(true);
  };

  const handleEditCategory = (category: KnowledgeCategory, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingCategory(category);
    setShowCategoryForm(true);
  };

  const handleDeleteClick = (categoryId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteConfirm(categoryId);
  };

  const handleConfirmDelete = async (categoryId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await KnowledgeCategoryService.deleteCategory(categoryId, true);
      setCategories(categories.filter(c => c.category_id !== categoryId));
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Error deleting category:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete category');
    }
  };

  const handleCancelDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteConfirm(null);
  };

  const handleFormClose = () => {
    setShowCategoryForm(false);
    setEditingCategory(null);
  };

  const handleFormSuccess = () => {
    setShowCategoryForm(false);
    setEditingCategory(null);
    loadCategories();
  };

  const handleToggleFavorite = async (category: KnowledgeCategory, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const result = await KnowledgeCategoryService.toggleFavorite(category.category_id);
      // Update the category in the state
      setCategories(prev => 
        prev.map(c => 
          c.category_id === category.category_id 
            ? { ...c, is_favorite: result.is_favorite } 
            : c
        )
      );
    } catch (err) {
      console.error('Error toggling favorite:', err);
      // Optionally show an error to the user
    }
  };

  const handleCategoryClick = (category: KnowledgeCategory) => {
    navigate(`/knowledge/${category.category_id}`);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const getOwnerName = (category: KnowledgeCategory): string => {
    return category.owner_display_name || category.owner_username || 'Unknown User';
  };

  const isOwner = (category: KnowledgeCategory): boolean => {
    if (!user) return false;
    return String(category.user_id) === String(user.id);
  };

  // Separate categories into owned and others
  const myCategories = categories.filter(cat => isOwner(cat));
  const otherCategories = categories.filter(cat => !isOwner(cat));
  const favoriteCategories = categories.filter(cat => cat.is_favorite);

  // Get current categories based on active tab
  const currentCategories = activeTab === 'my' 
    ? myCategories 
    : activeTab === 'other' 
    ? otherCategories
    : favoriteCategories;

  const renderCategoryCard = (category: KnowledgeCategory) => {
    const owned = isOwner(category);
    
    return (
      <div
        key={category.category_id}
        className="knowledge-card"
        onClick={() => handleCategoryClick(category)}
      >
        {category.image_url && (
          <div className="knowledge-card-image">
            <img 
              src={category.image_url} 
              alt={category.category_name}
              className="knowledge-card-img"
            />
          </div>
        )}
        <div className="knowledge-card-content">
          <h3 className="knowledge-card-title">{category.category_name}</h3>
          {category.description && (
            <p className="knowledge-card-description">{category.description}</p>
          )}
          <div className="knowledge-card-meta">
            {!owned && (
              <span className="knowledge-card-owner-badge">
                by {getOwnerName(category)}
              </span>
            )}
            <span className="knowledge-card-date">
              {formatDate(category.created_at)}
            </span>
          </div>
        </div>
        <div className="knowledge-card-actions-wrapper">
          <button
            className={`knowledge-card-favorite-btn ${category.is_favorite ? 'favorited' : ''}`}
            onClick={(e) => handleToggleFavorite(category, e)}
            aria-label="Toggle favorite"
          >
            ★
          </button>
          {owned && (
            <div className="knowledge-card-actions">
              <button
                className="knowledge-card-edit-btn"
                onClick={(e) => handleEditCategory(category, e)}
                aria-label="Edit category"
              >
                ✎
              </button>
              {deleteConfirm === category.category_id ? (
                <div className="knowledge-card-delete-confirm">
                  <button
                    className="knowledge-card-confirm-btn"
                    onClick={(e) => handleConfirmDelete(category.category_id, e)}
                  >
                    Delete
                  </button>
                  <button
                    className="knowledge-card-cancel-btn"
                    onClick={handleCancelDelete}
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  className="knowledge-card-delete-btn"
                  onClick={(e) => handleDeleteClick(category.category_id, e)}
                  aria-label="Delete category"
                >
                  ×
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="knowledge-categories-container">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={
          <h1 className="page-header-center-title">Knowledge Categories</h1>
        }
        rightActions={
          <button 
            className="page-header-icon-btn knowledge-add-btn"
            onClick={handleAddCategory}
            aria-label="Add category"
          >
            +
          </button>
        }
      />

      <div className="knowledge-main-content">
        {loading && (
          <div className="knowledge-loading">
            <div className="knowledge-spinner"></div>
            <p>Loading categories...</p>
          </div>
        )}

        {error && !loading && (
          <div className="knowledge-error">
            <p>{error}</p>
            <button 
              className="knowledge-retry-btn"
              onClick={loadCategories}
            >
              Try Again
            </button>
          </div>
        )}

        {!loading && !error && categories.length === 0 && (
          <div className="knowledge-empty">
            <div className="knowledge-empty-icon">🧠</div>
            <h2>No Categories Yet</h2>
            <p>Create your first knowledge category to start learning!</p>
            <button 
              className="knowledge-create-first-btn"
              onClick={handleAddCategory}
            >
              Create Category
            </button>
          </div>
        )}

        {!loading && !error && categories.length > 0 && (
          <>
            <div className="knowledge-grid">
              {currentCategories.map(category => renderCategoryCard(category))}
            </div>
            {currentCategories.length === 0 && (
              <div className="knowledge-empty-tab">
                <div className="knowledge-empty-icon">🧠</div>
                <h2>
                  {activeTab === 'my' 
                    ? 'No Categories Yet' 
                    : activeTab === 'other'
                    ? 'No Other Categories'
                    : 'No Favorite Categories'
                  }
                </h2>
                <p>
                  {activeTab === 'my' 
                    ? 'Create your first knowledge category to start learning!' 
                    : activeTab === 'other'
                    ? 'No categories from other users available yet.'
                    : 'Mark categories as favorites to see them here.'
                  }
                </p>
                {activeTab === 'my' && (
                  <button 
                    className="knowledge-create-first-btn"
                    onClick={handleAddCategory}
                  >
                    Create Category
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Bottom Tab Navigation */}
      {!loading && !error && categories.length > 0 && (
        <div className="knowledge-bottom-tabs">
          <button
            className={`knowledge-tab ${activeTab === 'my' ? 'knowledge-tab-active' : ''}`}
            onClick={() => setActiveTab('my')}
          >
            <span className="knowledge-tab-label">My Categories</span>
            {myCategories.length > 0 && (
              <span className="knowledge-tab-count">{myCategories.length}</span>
            )}
          </button>
          <button
            className={`knowledge-tab ${activeTab === 'other' ? 'knowledge-tab-active' : ''}`}
            onClick={() => setActiveTab('other')}
          >
            <span className="knowledge-tab-label">Other Categories</span>
            {otherCategories.length > 0 && (
              <span className="knowledge-tab-count">{otherCategories.length}</span>
            )}
          </button>
          <button
            className={`knowledge-tab ${activeTab === 'favorites' ? 'knowledge-tab-active' : ''}`}
            onClick={() => setActiveTab('favorites')}
          >
            <span className="knowledge-tab-label">Favorites</span>
            {favoriteCategories.length > 0 && (
              <span className="knowledge-tab-count">{favoriteCategories.length}</span>
            )}
          </button>
        </div>
      )}

      {showCategoryForm && (
        <CategoryForm
          category={editingCategory}
          onClose={handleFormClose}
          onSuccess={handleFormSuccess}
        />
      )}
    </div>
  );
};

export default KnowledgeCategories;

